/* ===========================================================================
   Cart drawer free shipping bar

   Threshold and both label strings come from the customiser (Theme settings >
   Cart > Free shipping bar) and reach us through theme.settings in
   layout/theme.liquid. The fallbacks below only apply when that block is
   missing — a stale cached layout, say — so the bar never renders empty.
   ========================================================================= */
const FREE_SHIPPING_FALLBACK_THRESHOLD = 15000; // cents
const FREE_SHIPPING_FALLBACK_PROGRESS = "[amount] away from **free shipping**";
const FREE_SHIPPING_FALLBACK_SUCCESS = "**Free shipping unlocked 🤙🏼**";

function freeShippingThreshold() {
  const settings = (window.theme && theme.settings) || {};
  const configured = Number(settings.freeShippingThreshold);
  return configured > 0 ? configured : FREE_SHIPPING_FALLBACK_THRESHOLD;
}

function escapeHtml(string) {
  return String(string)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// The label templates are merchant-entered, so they are escaped before any
// markup goes near them. **double asterisks** then become <strong>, and
// [amount] becomes the money value wrapped in a span — the same two elements
// the hardcoded strings used, so .free_shipping__label CSS is untouched.
function shippingLabel(template, fallback, amountHtml) {
  const text = typeof template === "string" && template.trim() !== "" ? template : fallback;

  return escapeHtml(text)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\[amount\]/g, () => amountHtml);
}

function checkShipping(subtotal) {
  const freeShippingElement = document.querySelector(".free_shipping");

  if (!freeShippingElement) return; // Exit if the element doesn't exist

  const settings = (window.theme && theme.settings) || {};
  const threshold = freeShippingThreshold();

  // Clamped: a threshold lowered below what is already in the cart would
  // otherwise push the indicator past the end of its track.
  const percent = Math.min(100, (subtotal / threshold) * 100);
  const remaining = Math.max(0, Math.round(threshold - subtotal));

  if (subtotal < threshold) {
    const amount =
      "<span>" + theme.Currency.formatMoney(remaining, settings.moneyFormat) + "</span>";

    freeShippingElement.innerHTML =
      "<div class='free_shipping__label'>" +
      shippingLabel(settings.freeShippingProgressText, FREE_SHIPPING_FALLBACK_PROGRESS, amount) +
      "</div><div class='pn-progressbar'><div class='pn-progressbar_ind' style='width:" +
      percent +
      "%'></div></div>";
  } else {
    const amount =
      "<span>" + theme.Currency.formatMoney(subtotal, settings.moneyFormat) + "</span>";

    freeShippingElement.innerHTML =
      "<div class='free_shipping__label'>" +
      shippingLabel(settings.freeShippingSuccessText, FREE_SHIPPING_FALLBACK_SUCCESS, amount) +
      "</div><div class='pn-progressbar'><div class='pn-progressbar_ind pn-progressbar_ind-comp' style='width:100%'></div></div>";
  }
}

const skateboardTypes = [
  '22" Skateboard',
  '27" Skateboard',
  '29" Skateboard',
  '32" Skateboard',
  '36" Skateboard',
];
const capTypes = ['Cap'];

function fetchCartContents() {
  const cartUrl = (window.theme && theme.routes && theme.routes.cart) || "/cart.js";

  fetch(cartUrl, { credentials: "same-origin" })
    .then((response) => response.json())
    .then((cart) => {
      checkCart(cart);
    })
    .catch((error) => {
      console.error("Error fetching cart details:", error);
    });
}

function checkCart(cart) {
  const hasSkateboard = cart.items.some((item) => skateboardTypes.includes(item.product_type));
  if (hasSkateboard) {
    displayBuyOneGetOne();
  } else {
    hideBuyOneGetOne();
  }

  const hasCap = cart.items.some((item) => capTypes.includes(item.product_type));
  if (hasCap) {
    hideCap();
  } else {
    displayCap();
  }

  CartUpsell.render(cart);
}

function displayBuyOneGetOne() {
  const div = document.querySelector(".cart-promo.buy-one-get-one");
  if (div) {
    div.style.display = "block";
  }
}

function hideBuyOneGetOne() {
  const div = document.querySelector(".cart-promo.buy-one-get-one");
  if (div) {
    div.style.display = "none";
  }
}

function displayCap() {
  const div = document.querySelector(".cart-promo.cap");
  if (div) {
    div.style.display = "block";
  }
}

function hideCap() {
  const div = document.querySelector(".cart-promo.cap");
  if (div) {
    div.style.display = "none";
  }
}

/* ===========================================================================
   Cart drawer upsells

   Renders sections/cart-drawer-upsell.liquid through the Section Rendering
   API. Complementary products (curated in the Shopify Search & Discovery app)
   are requested first; when the seed product has none defined Shopify returns
   an empty set and we fall back to intent=related, which is Shopify's
   automatic recommendation engine.

   The seed is the most recently added line item, so the suggestions follow
   what the customer just picked rather than being fixed to the first thing
   they added.
   ========================================================================= */
const CartUpsell = (function () {
  let inFlight = false;
  let lastKey = null;

  // Open by default. Kept at module scope so re-rendering after an add doesn't
  // spring the panel back open on someone who deliberately collapsed it.
  let collapsed = false;

  function mountEl() {
    return document.querySelector("[data-cart-upsell]");
  }

  function requestSection(mount, productId, intent) {
    const base = mount.dataset.recommendationsUrl || "/recommendations/products";
    const url =
      base +
      "?section_id=" + encodeURIComponent(mount.dataset.sectionId || "cart-drawer-upsell") +
      "&product_id=" + encodeURIComponent(productId) +
      "&limit=" + encodeURIComponent(mount.dataset.limit || 5) +
      "&intent=" + encodeURIComponent(intent);

    return fetch(url, { credentials: "same-origin" })
      .then((response) => (response.ok ? response.text() : ""))
      .catch(() => "");
  }

  // Shopify's recommendations don't exclude what's already in the cart, so
  // drop those slides here — suggesting something already added reads as a bug.
  function parseSection(html, excludeProductIds) {
    if (!html) return null;

    const holder = document.createElement("div");
    holder.innerHTML = html;

    const root = holder.querySelector("[data-upsell-root]");
    if (!root) return null;

    root.querySelectorAll("[data-upsell-slide]").forEach((slide) => {
      const id = parseInt(slide.dataset.productId, 10);
      if (excludeProductIds.indexOf(id) !== -1) slide.remove();
    });

    return root.querySelector("[data-upsell-slide]") ? root : null;
  }

  // Curated complementary products (Search & Discovery) always beat the
  // automatic engine, so every cart item gets a chance to supply them —
  // newest first — before falling back. Checking only the most recently added
  // item would miss curated sets on anything else already in the cart.
  const MAX_SEEDS = 5;

  async function resolveUpsells(mount, inCart) {
    const seeds = [];
    for (let i = inCart.length - 1; i >= 0 && seeds.length < MAX_SEEDS; i--) {
      if (seeds.indexOf(inCart[i]) === -1) seeds.push(inCart[i]);
    }

    for (const seed of seeds) {
      const html = await requestSection(mount, seed, "complementary");
      const root = parseSection(html, inCart);
      if (root) return root;
    }

    // Nothing curated anywhere in the cart — Shopify's recommendation engine,
    // anchored on the most recent addition.
    return parseSection(await requestSection(mount, seeds[0], "related"), inCart);
  }

  function render(cart) {
    const mount = mountEl();
    if (!mount) return;

    if (!cart || !cart.items || cart.items.length === 0) {
      mount.innerHTML = "";
      lastKey = null;
      return;
    }

    const inCart = cart.items.map((item) => item.product_id);

    // Re-fetching on every subtotal tick would flicker the slider for no gain.
    const key = inCart.join(",");
    if (key === lastKey || inFlight) return;

    inFlight = true;

    resolveUpsells(mount, inCart)
      .then((root) => {
        mount.innerHTML = "";
        if (root) {
          mount.appendChild(root);
          applyCollapsed(root);
        }
        lastKey = key;
        inFlight = false;
      })
      .catch(() => {
        inFlight = false;
      });
  }

  /* --- collapse ---------------------------------------------------------- */
  function applyCollapsed(root) {
    const toggle = root.querySelector("[data-upsell-toggle]");
    if (toggle) toggle.setAttribute("aria-expanded", collapsed ? "false" : "true");
  }

  function onToggle(button) {
    collapsed = button.getAttribute("aria-expanded") === "true";
    button.setAttribute("aria-expanded", collapsed ? "false" : "true");
  }

  /* --- add to cart ------------------------------------------------------- */
  function chosenVariantId(slide) {
    const select = slide.querySelector("[data-upsell-variant]");
    if (select) return select.value;

    const hidden = slide.querySelector("[data-upsell-id]");
    return hidden ? hidden.value : null;
  }

  function onAdd(event) {
    const toggle = event.target.closest("[data-upsell-toggle]");
    if (toggle) {
      event.preventDefault();
      onToggle(toggle);
      return;
    }

    const button = event.target.closest("[data-upsell-add]");
    if (!button) return;

    event.preventDefault();

    const slide = button.closest("[data-upsell-slide]");
    if (!slide) return;

    const variantId = chosenVariantId(slide);
    if (!variantId) return;

    // Keep the disabled mirror field in sync — the theme's own
    // ajaxProduct:added listener reads it off the event target.
    const hidden = slide.querySelector("[data-upsell-id]");
    if (hidden) hidden.value = variantId;

    const addUrl = (window.theme && theme.routes && theme.routes.cartAdd) || "/cart/add.js";

    button.classList.add("btn--loading");
    button.disabled = true;

    fetch(addUrl, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
    })
      .then((response) => response.json())
      .then((data) => {
        button.classList.remove("btn--loading");
        button.disabled = false;

        if (data && data.status && data.status !== 200) {
          slide.classList.add("cart-upsell__slide--error");
          console.warn("Upsell add failed:", data.description || data.message);
          return;
        }

        // Dispatched from the slide rather than document on purpose: the theme
        // listens for this and reads event.target.querySelector('[name="id"]'),
        // which only resolves if the target contains the field.
        slide.dispatchEvent(
          new CustomEvent("ajaxProduct:added", { bubbles: true, detail: { product: data } })
        );
      })
      .catch((error) => {
        button.classList.remove("btn--loading");
        button.disabled = false;
        console.error("Upsell add failed:", error);
      });
  }

  function bind() {
    const mount = mountEl();
    if (mount) mount.addEventListener("click", onAdd);
  }

  return { render: render, bind: bind };
})();

/* ===========================================================================
   Wiring
   ========================================================================= */
const cartDrawer = document.getElementById("CartDrawer");

if (cartDrawer) {
  CartUpsell.bind();

  const readSubtotalCents = function () {
    const node = cartDrawer.querySelector("[data-subtotal]");
    if (!node) return 0;

    return Math.round(parseFloat(node.textContent.trim().replace(/[^0-9.-]+/g, "")) * 100);
  };

  const refreshDrawer = function () {
    fetchCartContents();
    checkShipping(readSubtotalCents());
  };

  // Drawer opening: class flips to drawer--is-open.
  const drawerObserver = new MutationObserver(function (mutationsList) {
    for (let mutation of mutationsList) {
      if (mutation.type === "attributes" && mutation.attributeName === "class") {
        if (cartDrawer.classList.contains("drawer--is-open")) {
          refreshDrawer();
        }
      }
    }
  });

  drawerObserver.observe(cartDrawer, {
    attributes: true,
    attributeFilter: ["class"],
  });

  // Quantity changes and upsell adds rewrite the subtotal without touching the
  // drawer's class, so the progress bar and upsells need their own trigger.
  const subtotalElement = cartDrawer.querySelector("[data-subtotal]");

  if (subtotalElement) {
    const subtotalObserver = new MutationObserver(refreshDrawer);
    subtotalObserver.observe(subtotalElement, { childList: true, characterData: true, subtree: true });
  }
}
